import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, API, BRAND, formatApiError } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Sparkles, ShieldCheck, Clock,
  Calendar, Loader2, ChevronLeft, RefreshCw, Zap, Lock, Tag, X, Mail, Gift,
} from "lucide-react";

export default function BookSession() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestToken = searchParams.get("req") || "";
  const [sessionTypes, setSessionTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [schema, setSchema] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Admin-sent intake request (prefills contact + optional payment waiver)
  const [adminRequest, setAdminRequest] = useState(null);
  // Returning-user intake reuse
  const [savedIntake, setSavedIntake] = useState(null);
  const [intakeMeta, setIntakeMeta] = useState({ age_days: 0, needs_review: false, refresh_after_days: 49 });
  const [mode, setMode] = useState("new"); // 'reuse' | 'update' | 'new'

  useEffect(() => {
    (async () => {
      try {
        const [typesRes, schemaRes] = await Promise.all([
          api.get("/booking/session-types/public").then((r) => r.data).catch(() => null),
          api.get("/intake/questions"),
        ]);
        setSchema(schemaRes.data);
        if (typesRes && Array.isArray(typesRes) && typesRes.length) setSessionTypes(typesRes);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load form.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // If arriving with ?req=<token>, hydrate the admin-sent intake request.
  useEffect(() => {
    if (!requestToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/intake-request/${requestToken}`);
        setAdminRequest(data);
        setContact((c) => ({
          ...c,
          name: data.name || c.name,
          email: data.email || c.email,
        }));
        // Auto-select the pre-chosen session type once sessionTypes load
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "This intake link is no longer valid.");
      }
    })();
  }, [requestToken]);

  // Once session types load, honor the admin-request's pre-selection.
  useEffect(() => {
    if (!adminRequest || !sessionTypes.length) return;
    const match = sessionTypes.find((s) => s.session_type_id === adminRequest.session_type_id);
    if (match) {
      setSelectedType(match);
      setStep(1); // jump past the session picker
    }
  }, [adminRequest, sessionTypes]);

  // Detect a saved intake for logged-in users so they can skip the 42-question form
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/intake/mine");
        if (data?.has_intake) {
          setSavedIntake(data.intake);
          setIntakeMeta({
            age_days: data.age_days || 0,
            needs_review: !!data.needs_review,
            refresh_after_days: data.refresh_after_days || 49,
          });
          setMode(data.needs_review ? "update" : "reuse");
          // Pre-fill contact from saved intake
          setContact({
            name: data.intake.name || user.name || "",
            email: data.intake.email || user.email || "",
            phone: data.intake.phone || "",
          });
          setAnswers({ ...(data.intake.answers || {}) });
        }
      } catch { /* not fatal */ }
    })();
  }, [user]);

  // Pre-select the admin-marked "default for returning members" session type
  // once the session types have loaded. Applies to guests too so the page
  // matches what a logged-in member sees (per product spec).
  useEffect(() => {
    if (!sessionTypes.length || selectedType) return;
    const preferred = sessionTypes.find((s) => s.is_default_returning);
    if (preferred) setSelectedType(preferred);
  }, [sessionTypes, selectedType]);

  const sectionsInOrder = schema?.sections || [];
  const questionsBySection = useMemo(() => {
    if (!schema) return {};
    const map = {};
    sectionsInOrder.forEach((s) => (map[s] = []));
    schema.questions.forEach((q) => {
      if (!map[q.section]) map[q.section] = [];
      map[q.section].push(q);
    });
    // Personal Info questions are captured via the contact fields at top of step 1
    return map;
  }, [schema, sectionsInOrder]);

  const totalSteps = 1 + sectionsInOrder.length + 1; // pick + N sections + review
  const currentSection = step >= 1 && step <= sectionsInOrder.length
    ? sectionsInOrder[step - 1] : null;

  const setAnswer = (key, value) =>
    setAnswers((a) => ({ ...a, [key]: value }));

  const validateSection = (section) => {
    const qs = questionsBySection[section] || [];
    for (const q of qs) {
      // Personal info fields also come from `contact` state — mirror them
      if (["email", "phone", "full_name"].includes(q.key)) continue;
      if (!q.required) continue;
      const v = answers[q.key];
      if (q.type === "multi_choice") {
        if (!v || !Array.isArray(v) || v.length === 0) {
          toast.error(`Please answer: ${q.label}`);
          return false;
        }
      } else if (q.type === "consent") {
        if (v !== true) { toast.error(`Please accept: ${q.label.slice(0, 60)}…`); return false; }
      } else {
        if (v === undefined || v === null || String(v).trim() === "") {
          toast.error(`Please answer: ${q.label}`);
          return false;
        }
      }
    }
    // Personal info section — validate contact fields
    if (section === "Personal Information") {
      if (!contact.name.trim() || !contact.email.trim() || !contact.phone.trim()) {
        toast.error("Please fill in your name, email, and phone.");
        return false;
      }
      // Mirror into answers for the server
      setAnswers((a) => ({
        ...a,
        full_name: contact.name,
        email: contact.email,
        phone: contact.phone,
      }));
    }
    return true;
  };

  const next = () => {
    if (currentSection && !validateSection(currentSection)) return;
    setStep((s) => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (opts = {}) => {
    if (!selectedType) { toast.error("Pick a session type"); setStep(0); return; }

    // Returning user reuse path — skip questionnaire, jump to Stripe with saved intake
    if (user && savedIntake && mode === "reuse") {
      setSubmitting(true);
      try {
        // Payload uses saved answers + updates any timestamp for review history
        await api.patch("/intake/mine", { confirm_no_changes: true }).catch(() => {});
        const { data } = await api.post("/intake/submit", {
          session_type_id: selectedType.session_type_id,
          name: savedIntake.name,
          email: savedIntake.email,
          phone: savedIntake.phone,
          answers: savedIntake.answers || {},
          origin_url: window.location.origin,
          promo_code: (opts && opts.promo_code) || undefined,
          request_token: requestToken || undefined,
        });
        if (data?.free_granted) {
          toast.success(`Session unlocked with code ${data.promo_code}!`);
          window.location.href = "/my-intake";
          return;
        }
        if (data?.checkout_url) window.location.href = data.checkout_url;
        else toast.error("Payment redirect failed. Please try again.");
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Submission failed.");
        setSubmitting(false);
      }
      return;
    }

    // Default: full questionnaire path
    for (const s of sectionsInOrder) {
      if (!validateSection(s)) return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/intake/submit", {
        session_type_id: selectedType.session_type_id,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        answers: { ...answers, full_name: contact.name, email: contact.email, phone: contact.phone },
        origin_url: window.location.origin,
        promo_code: (opts && opts.promo_code) || undefined,
        request_token: requestToken || undefined,
      });
      if (data?.free_granted) {
        toast.success(`Session unlocked with code ${data.promo_code}!`);
        window.location.href = "/my-intake";
        return;
      }
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Payment redirect failed. Please try again.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Submission failed.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppShell guest={!user}>
        <div className="flex items-center gap-3 text-white/60 py-10"><Loader2 className="animate-spin" size={16}/> Preparing your intake…</div>
      </AppShell>
    );
  }

  const reuseMode = user && savedIntake && mode === "reuse";
  const totalStepsFinal = reuseMode ? 1 : totalSteps;

  return (
    <AppShell guest={!user}>
      <div className="max-w-3xl mx-auto" data-testid="book-session-page">
      {/* Admin-sent intake request banner */}
      {adminRequest && !adminRequest.already_used && (
        <div className="mb-6 rounded-2xl border border-[#FF8A00]/30 bg-gradient-to-br from-[#FF8A00]/8 to-[#E52E71]/4 p-5" data-testid="admin-request-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center shrink-0">
              <Mail size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">
                Personal invitation from {adminRequest.sent_by_name || "Dr. Slava"}
              </div>
              <div className="font-display text-xl mt-1">
                Your {adminRequest.session_type_title} intake is ready.
              </div>
              {adminRequest.personal_message && (
                <div className="mt-3 text-sm text-white/70 italic border-l-2 border-[#FF8A00]/40 pl-3">
                  "{adminRequest.personal_message}"
                </div>
              )}
              {adminRequest.waive_payment && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] tracking-wide px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  <Gift size={11} /> Complimentary — no payment required
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {adminRequest?.already_used && (
        <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          You've already used this intake link. If you need to submit a new intake, please contact Dr. Slava.
        </div>
      )}

      {/* Returning-user banner */}
      {user && savedIntake && !adminRequest && (
        <ReturningUserBanner
          intake={savedIntake}
          meta={intakeMeta}
          mode={mode}
          onReuse={() => setMode("reuse")}
          onReview={() => { setMode("update"); setStep(1); }}
          onFresh={() => { setMode("new"); setAnswers({}); setStep(1); }}
        />
      )}

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">
            {step === 0 ? "Pick a session" :
             step === totalStepsFinal - 1 ? "Review & pay" :
             `Section ${step} of ${sectionsInOrder.length}`}
          </div>
          <div className="text-xs text-white/40">{Math.round((step / Math.max(1, totalStepsFinal - 1)) * 100)}%</div>
        </div>
        <div className="h-1 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] transition-all duration-500"
            style={{ width: `${(step / Math.max(1, totalStepsFinal - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Step 0 — session picker */}
      {step === 0 && (
        <SessionPicker
          sessionTypes={sessionTypes}
          selected={selectedType}
          onSelect={setSelectedType}
          onNext={() => {
            if (!selectedType) return toast.error("Please pick a session type");
            // If returning user in reuse mode, jump straight to review/pay
            if (reuseMode) return submit();
            setStep(1);
          }}
          reuseMode={reuseMode}
        />
      )}

      {/* Section steps — hidden for reuse mode */}
      {!reuseMode && step >= 1 && step <= sectionsInOrder.length && (
        <SectionStep
          section={currentSection}
          questions={questionsBySection[currentSection] || []}
          answers={answers}
          setAnswer={setAnswer}
          contact={contact}
          setContact={setContact}
          isPersonalInfo={currentSection === "Personal Information"}
        />
      )}

      {/* Review step */}
      {!reuseMode && step === totalSteps - 1 && (
        <ReviewStep
          selectedType={selectedType}
          contact={contact}
          answers={answers}
          schema={schema}
          submitting={submitting}
          onSubmit={submit}
        />
      )}

      {/* Nav buttons */}
      {!reuseMode && step > 0 && step !== totalSteps - 1 && (
        <div className="flex items-center justify-between mt-10">
          <Button
            onClick={back}
            variant="outline"
            data-testid="intake-back"
            className="rounded-full bg-transparent border-white/15 hover:bg-white/5"
          >
            <ArrowLeft size={14} className="mr-2" /> Back
          </Button>
          <Button
            onClick={next}
            disabled={currentSection === "Consent & Acknowledgment"
              && (answers.consent_ack !== true
                  || !(answers.signature || "").trim())}
            data-testid="intake-next"
            className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40"
          >
            Continue <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
      )}
      {!reuseMode && step === totalSteps - 1 && (
        <div className="flex items-center justify-between mt-6">
          <Button onClick={back} variant="outline"
                  className="rounded-full bg-transparent border-white/15 hover:bg-white/5"
                  data-testid="intake-back">
            <ArrowLeft size={14} className="mr-2" /> Back
          </Button>
        </div>
      )}
      </div>
    </AppShell>
  );
}

// Returning-user reuse banner
function ReturningUserBanner({ intake, meta, mode, onReuse, onReview, onFresh }) {
  const updated = intake.updated_at || intake.created_at;
  const when = updated ? new Date(updated).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
  return (
    <div className="glass rounded-2xl p-5 md:p-6 mb-8 border border-[#FF8A00]/25" data-testid="returning-user-banner">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-11 h-11 rounded-lg bg-[#FF8A00]/15 text-[#FF8A00] flex items-center justify-center shrink-0">
          {meta.needs_review ? <RefreshCw size={20}/> : <Zap size={20}/>}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#FF8A00]">
            {meta.needs_review ? "Time for a quick refresh" : "Welcome back"}
          </div>
          <h2 className="font-display text-lg md:text-xl mt-1">
            {meta.needs_review
              ? `It's been ${meta.age_days} days since your last intake — anything changed?`
              : `Your intake from ${when} is on file.`}
          </h2>
          <p className="text-sm text-white/55 mt-1">
            {meta.needs_review
              ? "Take a minute to review your answers, or skip if nothing's changed."
              : `Skip the questionnaire and pick a session, or update your answers if things have changed.`}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={onReuse}
          data-testid="reuse-intake-btn"
          className={`rounded-full ${mode === "reuse" ? "bg-gradient-to-r from-[#FF8A00] to-[#E52E71]" : "bg-white/5 border border-white/10"}`}
        >
          <Zap size={13} className="mr-1.5"/> Use my saved intake
        </Button>
        <Button
          variant="outline"
          onClick={onReview}
          data-testid="review-intake-btn"
          className="rounded-full bg-transparent border-white/15 hover:bg-white/5"
        >
          <RefreshCw size={13} className="mr-1.5"/> Review &amp; update
        </Button>
        <Button
          variant="ghost"
          onClick={onFresh}
          data-testid="fresh-intake-btn"
          className="rounded-full text-white/50 hover:text-white hover:bg-white/5"
        >
          Start from scratch
        </Button>
      </div>
    </div>
  );
}

// ------------ Sub-components ------------

function SessionPicker({ sessionTypes, selected, onSelect, onNext, reuseMode }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">Book with Dr. Slava</div>
      <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">
        {reuseMode ? "Pick your session," : "Pick your session,"}<br />
        <span className="text-brand-gradient">{reuseMode ? "then head to checkout." : "then tell us about you."}</span>
      </h1>
      <p className="text-white/55 mt-4 max-w-lg">
        {reuseMode
          ? "You're all set — pick a session and we'll take you straight to payment."
          : "A short intake so Dr. Slava can prepare a personalized protocol before you meet."}
      </p>
      <div className="mt-8 space-y-3" data-testid="session-picker">
        {(sessionTypes.length ? sessionTypes : DEFAULT_TYPES).map((st) => {
          const active = selected?.session_type_id === st.session_type_id;
          return (
            <button
              key={st.session_type_id}
              onClick={() => onSelect(st)}
              data-testid={`session-type-${st.session_type_id}`}
              className={`w-full text-left glass rounded-2xl p-5 border-2 transition-all ${
                active ? "border-[#FF8A00] bg-[#FF8A00]/[0.04]" : "border-white/8 hover:border-white/20"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-display text-xl">{st.title}</div>
                  <div className="text-sm text-white/55 mt-1 leading-relaxed">{st.description}</div>
                  <div className="flex items-center gap-3 mt-3 text-xs text-white/45">
                    <span className="flex items-center gap-1.5"><Clock size={12}/> {st.duration_min} min</span>
                    <span>·</span>
                    <span className="flex items-center gap-1.5"><Calendar size={12}/> One-on-one with Dr. Slava</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-3xl">${(st.price_cents / 100).toFixed(0)}</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 mt-1">One-time</div>
                </div>
              </div>
              {active && (
                <div className="mt-4 flex items-center gap-2 text-xs text-[#FF8A00]">
                  <CheckCircle2 size={14} /> Selected
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-8 flex items-center justify-end">
        <Button
          onClick={onNext}
          disabled={!selected}
          data-testid="picker-continue"
          className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40"
        >
          {reuseMode ? <>Continue to payment <ArrowRight size={14} className="ml-2" /></> : <>Begin intake <ArrowRight size={14} className="ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}

const DEFAULT_TYPES = [
  { session_type_id: "st_initial", title: "Initial Protocol Session",
    description: "30-min deep dive with Dr. Slava — baseline review, peptide/hormone protocol design, action plan.",
    duration_min: 30, price_cents: 18000 },
  { session_type_id: "st_followup", title: "Follow-Up Session",
    description: "30-min check-in — adjust your protocol, review labs, refine.",
    duration_min: 30, price_cents: 10000 },
];

function SectionStep({ section, questions, answers, setAnswer, contact, setContact, isPersonalInfo }) {
  return (
    <div>
      <h2 className="font-display text-3xl sm:text-4xl">{section}</h2>
      <p className="text-white/45 text-sm mt-2">All fields marked with an asterisk are required.</p>

      <div className="mt-8 space-y-6" data-testid={`section-${section.replace(/[^a-z0-9]/gi,'-').toLowerCase()}`}>
        {isPersonalInfo && (
          <>
            <QField label="Full name *" testid="q-contact-name">
              <Input value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                placeholder="Jane Doe"
                className="bg-black/40 border-white/10 focus:border-[#FF8A00] h-11" />
            </QField>
            <QField label="Email *" testid="q-contact-email">
              <Input type="email" value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                placeholder="you@domain.com"
                className="bg-black/40 border-white/10 focus:border-[#FF8A00] h-11" />
            </QField>
            <QField label="Phone *" testid="q-contact-phone">
              <Input type="tel" value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                placeholder="+1 555 123 4567"
                className="bg-black/40 border-white/10 focus:border-[#FF8A00] h-11" />
            </QField>
          </>
        )}
        {questions
          .filter((q) => !isPersonalInfo || !["full_name", "email", "phone"].includes(q.key))
          .map((q) => (
            <QuestionField key={q.key} q={q}
              value={answers[q.key]} onChange={(v) => setAnswer(q.key, v)} />
        ))}
      </div>
    </div>
  );
}

function QField({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-[11px] tracking-[0.2em] uppercase text-white/50 block mb-2">{label}</label>
      {children}
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  const testid = `q-${q.key}`;
  const label = <>
    {q.label}{q.required && <span className="text-[#FF8A00] ml-1">*</span>}
  </>;
  const hint = q.hint && <div className="text-xs text-white/40 mt-1.5">{q.hint}</div>;

  if (q.type === "short_text" || q.type === "phone" || q.type === "email") {
    return (
      <QField label={label} testid={testid}>
        <Input type={q.type === "email" ? "email" : q.type === "phone" ? "tel" : "text"}
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="bg-black/40 border-white/10 focus:border-[#FF8A00] h-11" />
        {hint}
      </QField>
    );
  }
  if (q.type === "long_text") {
    return (
      <QField label={label} testid={testid}>
        <Textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="bg-black/40 border-white/10 focus:border-[#FF8A00] resize-none" />
        {hint}
      </QField>
    );
  }
  if (q.type === "number") {
    return (
      <QField label={label} testid={testid}>
        <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          className="bg-black/40 border-white/10 focus:border-[#FF8A00] h-11 w-40" />
        {hint}
      </QField>
    );
  }
  if (q.type === "date") {
    return (
      <QField label={label} testid={testid}>
        <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="bg-black/40 border-white/10 focus:border-[#FF8A00] h-11 w-56" />
        {hint}
      </QField>
    );
  }
  if (q.type === "yes_no") {
    return (
      <QField label={label} testid={testid}>
        <RadioGroup value={value || ""} onValueChange={onChange} className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
            <RadioGroupItem value="yes" /> Yes
          </label>
          <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
            <RadioGroupItem value="no" /> No
          </label>
        </RadioGroup>
        {hint}
      </QField>
    );
  }
  if (q.type === "single_choice") {
    return (
      <QField label={label} testid={testid}>
        <RadioGroup value={value || ""} onValueChange={onChange} className="space-y-1.5">
          {q.options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm text-white/80 cursor-pointer p-2 rounded-md hover:bg-white/[0.03]">
              <RadioGroupItem value={opt} /> {opt}
            </label>
          ))}
        </RadioGroup>
        {hint}
      </QField>
    );
  }
  if (q.type === "multi_choice") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      const next = arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt];
      onChange(next);
    };
    return (
      <QField label={label} testid={testid}>
        <div className="grid sm:grid-cols-2 gap-1.5">
          {q.options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm text-white/80 cursor-pointer p-2 rounded-md hover:bg-white/[0.03]">
              <Checkbox checked={arr.includes(opt)} onCheckedChange={() => toggle(opt)}
                className="border-white/25 data-[state=checked]:bg-[#FF8A00] data-[state=checked]:border-[#FF8A00]" />
              <span>{opt}</span>
            </label>
          ))}
        </div>
        {q.allow_other && (
          <Input placeholder="Other (please specify)"
            value={arr.find((x) => x.startsWith("Other:")) ? arr.find((x) => x.startsWith("Other:")).slice(7).trim() : ""}
            onChange={(e) => {
              const otherText = e.target.value.trim();
              const cleaned = arr.filter((x) => !x.startsWith("Other:"));
              onChange(otherText ? [...cleaned, `Other: ${otherText}`] : cleaned);
            }}
            className="mt-2 bg-black/40 border-white/10 focus:border-[#FF8A00] h-10" />
        )}
        {hint}
      </QField>
    );
  }
  if (q.type === "scale") {
    return (
      <QField label={label} testid={testid}>
        <div className="flex items-center gap-1.5 flex-wrap">
          {[1,2,3,4,5,6,7,8,9,10].map((n) => (
            <button key={n} type="button"
              onClick={() => onChange(String(n))}
              className={`w-10 h-10 rounded-lg border text-sm font-medium transition ${
                String(value) === String(n)
                  ? "border-[#FF8A00] bg-[#FF8A00]/15 text-white"
                  : "border-white/10 text-white/60 hover:border-white/25"
              }`}>
              {n}
            </button>
          ))}
        </div>
        {hint}
      </QField>
    );
  }
  if (q.type === "consent") {
    return (
      <QField label="" testid={testid}>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={value === true} onCheckedChange={(v) => onChange(!!v)}
            className="mt-1 border-white/25 data-[state=checked]:bg-[#FF8A00] data-[state=checked]:border-[#FF8A00]" />
          <span className="text-sm text-white/75 leading-relaxed">
            {q.label}{q.required && <span className="text-[#FF8A00] ml-1">*</span>}
          </span>
        </label>
      </QField>
    );
  }
  return null;
}

function ReviewStep({ selectedType, contact, answers, schema, submitting, onSubmit }) {
  const summary = [];
  schema?.sections?.forEach((s) => {
    (schema.questions.filter((q) => q.section === s)).forEach((q) => {
      const v = answers[q.key];
      if (v === undefined || v === null || (Array.isArray(v) && v.length === 0) || v === "") return;
      summary.push({ section: s, label: q.label, value: v });
    });
  });
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);

  const applyPromo = async () => {
    const code = (promoInput || "").trim();
    if (!code) return;
    setPromoChecking(true);
    setPromoError("");
    try {
      const { data } = await api.post("/promo/validate", {
        code,
        kind: "session_type",
        item_id: selectedType.session_type_id,
      });
      setPromo(data);
    } catch (e) {
      setPromo(null);
      setPromoError(formatApiError(e?.response?.data?.detail) || "Invalid code");
    } finally {
      setPromoChecking(false);
    }
  };
  const clearPromo = () => { setPromo(null); setPromoInput(""); setPromoError(""); };

  const original = selectedType?.price_cents || 0;
  const displayCents = promo ? promo.final_cents : original;
  return (
    <div data-testid="review-step">
      <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">Almost done</div>
      <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">Ready to book.</h2>
      <p className="text-white/55 mt-4 max-w-lg">Review your details, then continue to secure Stripe checkout.</p>

      <div className="glass rounded-2xl p-6 mt-8 border border-white/8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-2xl">{selectedType?.title || "Session"}</div>
            <div className="text-sm text-white/50 mt-1">{selectedType?.duration_min} min · one-on-one with Dr. Slava</div>
            <div className="text-sm text-white/70 mt-3">
              <div>{contact.name}</div>
              <div className="text-white/50">{contact.email} · {contact.phone}</div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-3xl">${(displayCents / 100).toFixed(0)}</div>
            {promo && (
              <div className="text-[10px] mt-1">
                <span className="line-through text-white/40">${(original / 100).toFixed(0)}</span>
                <span className="ml-1 text-[#FF8A00]">− ${(promo.discount_cents / 100).toFixed(0)}</span>
              </div>
            )}
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 mt-1">Total</div>
          </div>
        </div>
      </div>

      {/* Promo code */}
      <div className="mt-4" data-testid="intake-promo-wrap">
        {promo ? (
          <div className="flex items-center justify-between rounded-md border border-[#FF8A00]/30 bg-[#FF8A00]/5 px-3 py-2 text-sm" data-testid="intake-promo-applied">
            <div className="flex items-center gap-2">
              <Tag size={14} className="text-[#FF8A00]" />
              <span className="font-mono">{promo.code}</span>
              <span className="text-white/60">applied</span>
            </div>
            <button type="button" onClick={clearPromo} className="text-white/40 hover:text-white" data-testid="intake-promo-clear">
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-stretch gap-2">
            <input
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
              placeholder="Promo code (optional)"
              className="flex-1 h-10 rounded-md bg-white/5 border border-white/10 px-3 text-sm uppercase placeholder:text-white/30 placeholder:normal-case focus:outline-none focus:border-[#FF8A00]/40"
              data-testid="intake-promo-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
            />
            <Button
              type="button"
              onClick={applyPromo}
              disabled={promoChecking || !promoInput.trim()}
              data-testid="intake-promo-apply"
              className="h-10 px-4 rounded-md bg-white/10 hover:bg-white/15 text-sm"
            >
              {promoChecking ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
            </Button>
          </div>
        )}
        {promoError && <div className="mt-1 text-[11px] text-red-400" data-testid="intake-promo-error">{promoError}</div>}
      </div>

      <details className="glass rounded-2xl p-4 mt-4 border border-white/6">
        <summary className="cursor-pointer text-sm text-white/75 hover:text-white">
          Preview your intake responses ({summary.length} answered)
        </summary>
        <div className="mt-4 space-y-3 text-sm max-h-96 overflow-y-auto pr-2">
          {summary.map((s, i) => (
            <div key={i} className="border-t border-white/5 pt-2 first:border-t-0 first:pt-0">
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">{s.section}</div>
              <div className="text-white/85 mt-1">{s.label}</div>
              <div className="text-white/60 mt-0.5 whitespace-pre-wrap">
                {Array.isArray(s.value) ? s.value.join(", ") : String(s.value)}
              </div>
            </div>
          ))}
        </div>
      </details>

      <Button
        onClick={() => onSubmit({ promo_code: promo?.code })}
        disabled={submitting}
        data-testid="intake-submit"
        className="w-full rounded-full h-14 mt-8 bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-white text-base disabled:opacity-60"
      >
        {submitting
          ? <><Loader2 className="animate-spin mr-2" size={16}/> {promo?.is_free ? "Unlocking…" : "Redirecting to Stripe…"}</>
          : promo?.is_free
            ? <>Unlock session (100% off) <ArrowRight size={16} className="ml-2"/></>
            : <>Continue to secure payment <ArrowRight size={16} className="ml-2"/></>}
      </Button>
      <div className="flex items-center justify-center gap-2 mt-3 text-[11px] text-white/40" data-testid="phi-encryption-note">
        <Lock size={12} /> Your health answers are encrypted at rest — only Dr. Slava can view them.
      </div>
      <div className="flex items-center justify-center gap-2 mt-2 text-[11px] text-white/40">
        <ShieldCheck size={12} /> Secured by Stripe · your card info never touches our servers.
      </div>
    </div>
  );
}
